import { normaliseDecimal } from '@/features/finance/lines'
import type { PlannedExpenseLineInput } from '@/features/cases/api/useCase'
import { UNIT_KINDS, unitKey } from '@/lib/legUnits'
import type { CaseLeg, LegUnit, LegUnitPayload, UnitKind } from '@/types/domain'

import { carries } from './unitKinds'

/* The unit cards as data — the rows the leg page's grids edit, and every rule
 * about them that a test can pin without rendering a table (S-10; every unit
 * kind since S-18b).
 *
 * The grids are a DRAFT. Nothing in them writes: the page's one save bar sends
 * the rows as the leg PUT's `units` and their rates as one batch of planned
 * cost lines. What this file decides is what those two writes carry, and what
 * the rows look like after each of them lands — which is where the hard parts
 * are:
 *
 *  - two trailing rows always wait under the last unit of EACH kind, untouched
 *    (`pristine`) and showing the defaults faintly; typing into one makes it a
 *    unit of that card's kind and puts a fresh one behind it, so "add a row" is
 *    never a step;
 *  - the rows are ONE list across every kind, grouped in `UNIT_KINDS` order —
 *    the order the PUT sends them in, which is the order the server numbers;
 *  - after a write the rows are RECONCILED, not re-seeded: each keeps its order
 *    and what was typed, and takes its server id by `unitKey(kind, number)` —
 *    the server's own per-kind fold, so a container and a B/L sharing a
 *    number keep their own ids and rates;
 *  - after a 409 `leg-units-changed` the stored units come back, a reader's
 *    unsaved rows and their edits to surviving units stay;
 *  - a rate is written once: a row whose batch landed is marked and never
 *    composed again, and a unit that already has a per-unit plan offers no
 *    input at all (AD-4 — that plan is edited on the money screen);
 *  - «Плечо целиком» (S-18a) follows the same rule for the LEG rate: one
 *    planned line with the leg and no unit, composed into the same batch
 *    while the leg has no leg-level plan, and marked written once it lands.
 */

/** How many units one leg may carry, every kind together. Mirrors
 *  `maxLegUnits` (backend/internal/casefile/legs.go); the server refuses past
 *  it (422 `units: too-long`), this refuses at input time. One batch books
 *  every unit's rate plus the leg rate, which is why `MaxExpenseLineBatch` is
 *  this plus one (backend/internal/finance/expense_service.go). */
export const MAX_LEG_UNITS = 100

/** The currencies a unit's rate is agreed in. */
export const RATE_CURRENCIES = ['USD', 'EUR', 'CNY', 'RUB', 'TMT'] as const

/** One row of a unit card — a saved unit, one being added, or a trailing
 *  blank. Every kind shares the shape; a field the kind does not carry stays
 *  `''` and is never sent (`unitPayloadOf`). */
export interface UnitRow {
  /** React's key, stable across every edit, reconciliation and conflict merge,
   *  so a row that becomes a unit under the cursor keeps the cursor. */
  key: string
  /** The saved unit's id; `''` until a leg write answers with it. */
  id: string
  /** The card the row belongs to. Never edited: a unit moved to another kind
   *  is a removal plus an insertion. */
  kind: UnitKind
  /** The plate, the container or wagon number, the AWB, the B/L… */
  number: string
  trailerPlate: string
  containerNumber: string
  cmrNumber: string
  sealNumber: string
  unitType: string
  carrierId: string
  carrierName: string
  driverName: string
  driverPhone: string
  /** Whole kilograms as typed — a half-typed "18 4" is not a number yet. */
  weightKg: string
  /** The agreed rate as typed. Only ever written by the batch, and only for a
   *  unit with no per-unit plan yet. */
  rate: string
  /** The rate's currency; each batch line carries its own row's. */
  currency: string
  /** A trailing blank: drawn with the defaults faintly, never sent. */
  pristine: boolean
  /** The batch that booked this row's rate landed. Never composed again. */
  rateWritten: boolean
}

/** What a new row is born with — the card header's «Новые строки:». */
export interface RowDefaults {
  carrierId: string
  carrierName: string
  rate: string
  currency: string
}

/** The fields a unit carries on the wire, in the order a merge walks them. */
const UNIT_ROW_FIELDS = [
  'number',
  'trailerPlate',
  'containerNumber',
  'cmrNumber',
  'sealNumber',
  'unitType',
  'carrierId',
  'carrierName',
  'driverName',
  'driverPhone',
  'weightKg',
] as const

let rowSeq = 0

function nextKey(): string {
  rowSeq += 1
  return `row-${rowSeq}`
}

/** A trailing blank row of one card. */
export function pristineRow(kind: UnitKind = 'truck'): UnitRow {
  return {
    key: nextKey(),
    id: '',
    kind,
    number: '',
    trailerPlate: '',
    containerNumber: '',
    cmrNumber: '',
    sealNumber: '',
    unitType: '',
    carrierId: '',
    carrierName: '',
    driverName: '',
    driverPhone: '',
    weightKg: '',
    rate: '',
    currency: '',
    pristine: true,
    rateWritten: false,
  }
}

/** A blank row becoming a unit: it takes the defaults as VALUES now, so what
 *  the reader saw faintly is what is sent — and a later change of the defaults
 *  does not move a unit somebody already started typing. */
export function materialize(row: UnitRow, defaults: RowDefaults): UnitRow {
  if (!row.pristine) return row
  return {
    ...row,
    carrierId: defaults.carrierId,
    carrierName: defaults.carrierName,
    rate: defaults.rate,
    currency: defaults.currency,
    pristine: false,
  }
}

/** A saved unit, flattened for editing. A suppressed value never loads into
 *  an input (AD-6): the four identity fields are blanked, and the page refuses
 *  the save instead (`legIdentityHidden`). */
export function savedRow(unit: LegUnit): UnitRow {
  const editable = (value: unknown): string => (typeof value === 'string' ? value : '')
  return {
    ...pristineRow(unit.kind),
    key: unit.id,
    id: unit.id,
    number: unit.number,
    trailerPlate: unit.trailerPlate ?? '',
    containerNumber: unit.containerNumber ?? '',
    cmrNumber: unit.cmrNumber ?? '',
    sealNumber: unit.sealNumber ?? '',
    unitType: unit.unitType ?? '',
    carrierId: editable(unit.carrierId),
    carrierName: editable(unit.carrierName),
    driverName: editable(unit.driverName),
    driverPhone: editable(unit.driverPhone),
    weightKg: unit.weightKg === undefined ? '' : String(unit.weightKg),
    pristine: false,
  }
}

/** The rows a page opens with: every saved unit grouped by kind, then each
 *  kind's two blanks. */
export function seedRows(units: readonly LegUnit[]): UnitRow[] {
  return withTrailing(units.map(savedRow))
}

/** One card's rows, in order. */
export function rowsOfKind(rows: readonly UnitRow[], kind: UnitKind): UnitRow[] {
  return rows.filter((row) => row.kind === kind)
}

/** Whether a row is, or is becoming, a unit. */
function counts(row: UnitRow): boolean {
  return !row.pristine && (row.id !== '' || row.number.trim() !== '')
}

/** The rows that are, or are becoming, units — what the leg's cap counts,
 *  every kind together. */
export function countedRows(rows: readonly UnitRow[]): number {
  return rows.filter(counts).length
}

/** The kinds that hold a unit or a row being typed — always shown, whatever
 *  the mode (S-18b). */
export function presentKinds(rows: readonly UnitRow[]): Set<UnitKind> {
  return new Set(rows.filter((row) => !row.pristine).map((row) => row.kind))
}

/** The rows grouped in `UNIT_KINDS` order with exactly two blanks after each
 *  kind's last real row, keeping the keys of the blanks already there — or
 *  none once the leg is full, so a 101st unit cannot be started. A blank
 *  stranded between units (the reader typed into the second one) stays where
 *  it is: it is never sent. */
export function withTrailing(rows: readonly UnitRow[], cap = MAX_LEG_UNITS): UnitRow[] {
  const groups = UNIT_KINDS.map((kind) => {
    const own = rowsOfKind(rows, kind)
    let end = own.length
    while (end > 0 && own[end - 1]?.pristine) end -= 1
    return { kind, real: own.slice(0, end), blanks: own.slice(end, end + 2) }
  })
  const full = countedRows(groups.flatMap((group) => group.real)) >= cap
  return groups.flatMap(({ kind, real, blanks }) => {
    if (full) return real
    const kept = [...blanks]
    while (kept.length < 2) kept.push(pristineRow(kind))
    return [...real, ...kept]
  })
}

/** One row changed. A blank takes the defaults first (`materialize`). */
export function updateRow(
  rows: readonly UnitRow[],
  key: string,
  patch: Partial<UnitRow>,
  defaults: RowDefaults,
): UnitRow[] {
  return withTrailing(
    rows.map((row) => (row.key === key ? { ...materialize(row, defaults), ...patch } : row)),
  )
}

/** One row out of the draft. Nothing is asked of the server: the save bar's
 *  PUT sends the list without it. */
export function removeRow(rows: readonly UnitRow[], key: string): UnitRow[] {
  return withTrailing(rows.filter((row) => row.key !== key))
}

/** A NEW unit row on a card's own account — the phone's «+ Фура». It lands
 *  after that kind's last unit; the kind's blanks are drawn again behind it
 *  (a phone never renders them). */
export function appendRow(rows: readonly UnitRow[], row: UnitRow): UnitRow[] {
  return withTrailing([
    ...rows.filter((candidate) => !(candidate.kind === row.kind && candidate.pristine)),
    { ...row, pristine: false },
  ])
}

/** «↓ всем»: a card header's default copied into every NEW row of that card —
 *  never into a saved unit, whose carrier and rate are facts somebody already
 *  agreed, and never into another card. */
export function fillNewRows(
  rows: readonly UnitRow[],
  kind: UnitKind,
  patch: Partial<Pick<UnitRow, 'carrierId' | 'carrierName' | 'rate' | 'currency'>>,
): UnitRow[] {
  return rows.map((row) => (row.kind === kind && row.id === '' && !row.pristine ? { ...row, ...patch } : row))
}

/** A row's server key: `unitKey(kind, number)`, the per-kind fold the server
 *  matches by (a truck's plate folded, every other number exact). */
export function rowKey(row: Pick<UnitRow, 'kind' | 'number'>): string {
  return unitKey(row.kind, row.number)
}

/** A list pasted into a number cell — five plates read off a dispatcher's
 *  message — as rows of that card. Null when the text is one line (an
 *  ordinary paste).
 *
 *  The first number lands in the row pasted into (unless that row already has
 *  one), the rest become new rows of the same kind under it with the defaults.
 *  Refused WHOLE, with the count, when it would take the leg past the cap:
 *  half a list is a list somebody has to diff against the original by eye. */
export function pasteRows(
  rows: readonly UnitRow[],
  key: string,
  text: string,
  defaults: RowDefaults,
  cap = MAX_LEG_UNITS,
): { rows: UnitRow[] } | { refused: { pasted: number; room: number } } | null {
  const numbers = text
    .split(/\r?\n/)
    .map((line) => (line.split('\t')[0] ?? '').trim())
    .filter((line) => line !== '')
  if (numbers.length < 2) return null

  const index = rows.findIndex((row) => row.key === key)
  const target = rows[index]
  if (target === undefined) return null
  const fillsTarget = target.number.trim() === ''
  const room = cap - countedRows(rows)
  if (numbers.length > room) return { refused: { pasted: numbers.length, room: Math.max(room, 0) } }

  const [first, ...rest] = numbers
  const added = (fillsTarget ? rest : numbers).map((number) => ({
    ...materialize(pristineRow(target.kind), defaults),
    number,
  }))
  const head = rows.slice(0, index + 1).map((row) =>
    row.key === key && fillsTarget ? { ...materialize(row, defaults), number: first ?? '' } : row,
  )
  return { rows: withTrailing([...head, ...added, ...rows.slice(index + 1)], cap) }
}

/** Accepts `1234.56`, `1 234,56`; rejects text and blanks — the finance
 *  sheets' own shape, because it lands in the same column. */
export function isRate(raw: string): boolean {
  const normalised = normaliseDecimal(raw)
  return normalised !== '' && /^\d+(\.\d{1,2})?$/.test(normalised)
}

/** Whole kilograms (0055). «18.4» is a typo here, not 18 400. */
export function isWeight(raw: string): boolean {
  return /^\d+$/.test(raw.trim())
}

/** What a row cannot send, per field — computed while typing, so the save is
 *  refused with a reason rather than dropping a figure under a success. A row
 *  with no number is not a unit and complains about nothing. `ratable` is
 *  whether this row offers a rate at all. */
export function rowProblems(
  row: UnitRow,
  ratable: boolean,
): { rate: boolean; weight: boolean } {
  if (row.pristine || row.number.trim() === '') return { rate: false, weight: false }
  return {
    rate: ratable && !row.rateWritten && row.rate.trim() !== '' && !isRate(row.rate),
    weight: carries(row.kind, 'weightKg') && row.weightKg.trim() !== '' && !isWeight(row.weightKg),
  }
}

/** One row as the leg PUT takes it — only the fields its kind carries, so the
 *  server never answers `not-for-kind` for a value no card asked. Blank
 *  optionals are dropped: the server reads an absent field as "nobody stated
 *  this". */
export function unitPayloadOf(row: UnitRow): LegUnitPayload {
  const text = (field: 'trailerPlate' | 'containerNumber' | 'cmrNumber' | 'sealNumber' | 'unitType' | 'driverName' | 'driverPhone') =>
    carries(row.kind, field) ? row[field].trim() || undefined : undefined
  return {
    kind: row.kind,
    number: row.number.trim(),
    trailerPlate: text('trailerPlate'),
    containerNumber: text('containerNumber'),
    cmrNumber: text('cmrNumber'),
    sealNumber: text('sealNumber'),
    unitType: text('unitType'),
    carrierId: row.carrierId.trim() || undefined,
    carrierName: row.carrierName.trim() || undefined,
    driverName: text('driverName'),
    driverPhone: text('driverPhone'),
    // Unparseable weights never reach here: `rowProblems` blocks the save.
    weightKg: carries(row.kind, 'weightKg') && isWeight(row.weightKg) ? Number(row.weightKg.trim()) : undefined,
  }
}

/** The whole list the PUT sends: every unit the rows hold, grouped by kind in
 *  `UNIT_KINDS` order, in the rows' order within a kind. */
export function unitsPayload(rows: readonly UnitRow[]): LegUnitPayload[] {
  return UNIT_KINDS.flatMap((kind) =>
    rowsOfKind(rows, kind)
      .filter((row) => !row.pristine && row.number.trim() !== '')
      .map(unitPayloadOf),
  )
}

/** Whether the rows say anything about the units the stored list does not —
 *  compared as payloads, both grouped the same way, so retyping a value and
 *  undoing it is no change and a migrated leg whose trucks and containers
 *  share positions is not "touched" by being read. */
export function unitsTouched(
  rows: readonly UnitRow[],
  stored: readonly LegUnit[],
): boolean {
  return (
    JSON.stringify(unitsPayload(rows)) !==
    JSON.stringify(unitsPayload(stored.map(savedRow)))
  )
}

/** After a leg write lands: each row takes its unit's id by `unitKey(kind,
 *  number)` and keeps everything typed into it. Order is the reader's. */
export function reconcileRows(
  rows: readonly UnitRow[],
  saved: readonly LegUnit[],
): UnitRow[] {
  const idByKey = new Map(saved.map((unit) => [unitKey(unit.kind, unit.number), unit.id]))
  return rows.map((row) => {
    if (row.pristine || row.number.trim() === '') return row
    const id = idByKey.get(rowKey(row))
    return id === undefined ? row : { ...row, id }
  })
}

/** A unit named by its kind and number — what the page says about it. */
export interface NamedUnit {
  kind: UnitKind
  number: string
}

/** After a 409 `leg-units-changed`: the stored units as they are now, with
 *  the reader's work laid over them.
 *
 *  Per FIELD, not per row: on a unit both readers touched, a field this reader
 *  changed keeps their value and every other field takes the stored one — so
 *  the colleague's corrected driver survives this reader's corrected phone. A
 *  saved unit this reader removed stays removed; a unit the colleague added
 *  appears; every unsaved row stays. `vanished` names the saved units this
 *  reader was editing that the colleague removed. */
export function mergeAfterConflict(
  rows: readonly UnitRow[],
  before: readonly LegUnit[],
  fresh: readonly LegUnit[],
): { rows: UnitRow[]; vanished: NamedUnit[] } {
  const mine = new Map(rows.filter((row) => row.id !== '').map((row) => [row.id, row]))
  const was = new Map(before.map((unit) => [unit.id, savedRow(unit)]))
  const freshIds = new Set(fresh.map((unit) => unit.id))

  const merged = fresh.flatMap((unit): UnitRow[] => {
    const theirs = savedRow(unit)
    const own = mine.get(unit.id)
    const old = was.get(unit.id)
    if (!own) return old ? [] : [theirs]
    const next: UnitRow = { ...own }
    for (const field of UNIT_ROW_FIELDS) {
      next[field] = old && own[field] !== old[field] ? own[field] : theirs[field]
    }
    return [next]
  })

  return {
    rows: withTrailing([...merged, ...rows.filter((row) => row.id === '')]),
    vanished: rows
      .filter((row) => row.id !== '' && !freshIds.has(row.id))
      .map((row) => ({ kind: row.kind, number: row.number.trim() })),
  }
}

/** Whether a row will put a rate in the next batch — read off what is typed,
 *  before any write has answered (the «Ставки детализируют» question depends on
 *  it). `planned` holds the units that already have a per-unit plan. */
export function rowWritesRate(row: UnitRow, planned: ReadonlySet<string>): boolean {
  return (
    !row.pristine &&
    !row.rateWritten &&
    row.number.trim() !== '' &&
    isRate(row.rate) &&
    !(row.id !== '' && planned.has(row.id))
  )
}

/** A planned line as this page composes it: a unit's rate (with its
 *  `unitId`) or, since S-18a, the leg rate (with none). */
export type RateLineInput = PlannedExpenseLineInput

/** A planned cost line with the unit it was composed for: the line is what
 *  the batch sends, the unit is what the page names when the line cannot be
 *  sent — the wire line itself carries no number, only the unit id. The leg
 *  rate's unit is `null`: it names no unit and is never unsendable. */
export interface RateLine {
  unit: NamedUnit | null
  line: RateLineInput
}

/** The planned cost lines the rows' rates become, keyed to the unit ids of the
 *  latest leg answer by `unitKey(kind, number)` — a rate is never posted
 *  against an id this client invented, and a container and a B/L sharing a
 *  number each get their own. No vendor travels with a planned line
 *  (`planned-has-no-vendor`): who runs the unit is on the unit.
 *
 *  A row's own id wins over the key lookup, and it can be stale: it is the
 *  answer the row was reconciled from, and rows holding unwritten rates are
 *  not re-seeded when a refetch lands. `splitByUnit` is the check that
 *  catches it, before any line reaches the batch. */
export function composeRates({
  rows,
  units,
  legId,
  planned,
  item,
  link,
}: {
  rows: readonly UnitRow[]
  units: CaseLeg['units']
  legId: string
  planned: ReadonlySet<string>
  /** The stored item wording of a unit's rate, per kind
   *  (`cases.legs.vehiclesTable.rateItem` for a truck, verbatim). */
  item: (unit: NamedUnit) => string
  /** The «Ставки детализируют» answer: a parent, the stored «не
   *  детализирует», or nothing (unanswered — never sent). */
  link: { detailOf: string } | { standsAlone: true } | Record<string, never>
}): RateLine[] {
  const idByKey = new Map(units.map((unit) => [unitKey(unit.kind, unit.number), unit.id]))
  return rows.flatMap((row) => {
    if (!rowWritesRate(row, planned)) return []
    const unitId = row.id || idByKey.get(rowKey(row))
    if (!unitId || planned.has(unitId)) return []
    const named = { kind: row.kind, number: row.number.trim() }
    return [
      {
        unit: named,
        line: {
          kind: 'planned' as const,
          item: item(named),
          legId,
          unitId,
          amount: { amount: normaliseDecimal(row.rate), currency: row.currency || 'USD' },
          ...link,
        },
      },
    ]
  })
}

/** The rate lines a press may batch, split by the unit list the server last
 *  answered with. The batch is all-or-none — one line naming a unit the leg
 *  no longer has is 422 `unitId: invalid` and refuses every other rate with
 *  it, on every retry — so a line whose unit is not in `units` is never sent;
 *  it comes back in `gone` for the page to name. Held and composed lines
 *  alike: a held line names the unit id of the answer it was composed
 *  against, and a composed one may carry a row's stale id (`composeRates`). */
export function splitByUnit(
  rates: readonly RateLine[],
  units: CaseLeg['units'],
): { send: RateLine[]; gone: RateLine[] } {
  const onLeg = new Set(units.map((unit) => unit.id))
  /* The leg rate names no unit, so no unit list can make it stale. */
  const sendable = ({ line }: RateLine) => line.unitId === undefined || onLeg.has(line.unitId)
  return {
    send: rates.filter(sendable),
    gone: rates.filter((rate) => !sendable(rate)),
  }
}

/** The rows whose rates a landed batch booked: marked, never composed again. */
export function markRatesWritten(
  rows: readonly UnitRow[],
  unitIds: ReadonlySet<string>,
): UnitRow[] {
  return rows.map((row) => (row.id !== '' && unitIds.has(row.id) ? { ...row, rateWritten: true } : row))
}

/** «Плечо целиком»'s rate as typed (S-18a) — the leg-level twin of a row's
 *  `rate` / `currency` / `rateWritten`. */
export interface LegRateDraft {
  rate: string
  currency: string
  /** The batch that booked it landed. Never composed again. */
  written: boolean
}

export function emptyLegRate(): LegRateDraft {
  return { rate: '', currency: 'USD', written: false }
}

/** A typed leg rate that cannot be sent — the save is refused with a reason
 *  rather than dropping the figure under a success. */
export function legRateProblem(draft: LegRateDraft): boolean {
  return !draft.written && draft.rate.trim() !== '' && !isRate(draft.rate)
}

/** The leg rate's planned line, or `null` when there is nothing to write: a
 *  blank, a malformed or a written figure, or a leg that already has a
 *  leg-level plan (S-10's rule — that plan is edited on the money screen).
 *  Filed under the same «Ставки детализируют» `link` as the unit rates: the
 *  leg rate is a truck rate for S-12 (owner). */
export function composeLegRate({
  draft,
  legId,
  hasLegPlan,
  item,
  link,
}: {
  draft: LegRateDraft
  legId: string
  hasLegPlan: boolean
  item: string
  link: { detailOf: string } | { standsAlone: true } | Record<string, never>
}): RateLine | null {
  if (hasLegPlan || draft.written || !isRate(draft.rate)) return null
  return {
    unit: null,
    line: {
      kind: 'planned',
      item,
      legId,
      amount: { amount: normaliseDecimal(draft.rate), currency: draft.currency || 'USD' },
      ...link,
    },
  }
}
